import { RequireSomeFields } from 'common/TypeUtilities';
import React from 'react';
import halalKibbeh from 'src/assets/img/category-pages/halal/halal-kibbeh.svg';
import halalLambTagine from 'src/assets/img/category-pages/halal/halal-lamb-tagine.svg';
import halalOuzieWithLegOfLamb from 'src/assets/img/category-pages/halal/halal-ouzi-with-leg-of-lamb.svg';
import { Chip, ChipColor } from 'src/shared/design-system/Chip';
import { Divider } from 'src/shared/design-system/Divider';
import { Modal, ModalBody, ModalProps } from 'src/shared/design-system/Modal';
import { VerticallyCenteredText } from 'src/shared/design-system/VerticallyCenteredText';
import styled from 'styled-components';
import { useScreenContext } from 'web-common/src/shared/context/ScreenContext/hook';
import { Colors, Font, Spacing } from 'web-common/src/shared/styles';

// NOTE: Currently, the header is always 56px, as we refactor to support more verified dietary modals, this may need
// to be a little more dynamic
const HEADER_HEIGHT = 56;

const StyledModal = styled(Modal)`
  padding: 0;
  row-gap: 0;
`;

const Header = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: ${Spacing.SINGLE} ${Spacing.SINGLE} ${Spacing.SINGLE} ${Spacing.TRIPLE_HALF};
  position: relative;

  height: ${HEADER_HEIGHT}px;

  font-family: ${Font.Family.SANS};
  font-style: normal;
  font-weight: ${Font.Weight.NORMAL};
  font-size: ${Font.Size.M};
  line-height: 24px;
  color: ${Colors.GRAY_DARK_30};
`;

const BackButtonContainer = styled.div`
  width: 48px;
  height: 100%;
  position: absolute;
  top: 0;
  left: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
`;
const BackButton = styled.div`
  border-radius: 100%;
  border: 2px solid ${Colors.GRAY_LITE_20};
  width: 24px;
  height: 24px;

  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
`;

const StyledModalBody = styled(ModalBody)`
  max-height: calc(100dvh - ${HEADER_HEIGHT}px);
`;

const Section = styled.section<{ background?: string }>`
  display: flex;
  flex-direction: column;
  padding: ${Spacing.SINGLE_HALF} ${Spacing.SINGLE};
  background: ${({ background }) => background ?? Colors.WHITE};
`;

const SectionTitle = styled.h2`
  font-family: ${Font.Family.SANS};
  font-style: normal;
  font-weight: ${Font.Weight.BOLD};
  font-size: ${Font.Size.XL};
  color: ${Colors.GRAY_DARK_30};
`;

const SectionText = styled.span`
  font-family: ${Font.Family.SANS};
  font-style: normal;
  font-weight: ${Font.Weight.MEDIUM};
  font-size: ${Font.Size.S};
  color: ${Colors.GRAY_DARK_15};
`;

const SectionFootnote = styled(SectionText)`
  font-style: italic;
  font-size: ${Font.Size.XS};
`;

const ChipsJumble = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: flex-start;
  gap: ${Spacing.HALF};
`;

const StyledChip = styled(Chip)`
  height: 32px;
  padding: ${Spacing.HALF};
  width: fit-content;
  font-weight: ${Font.Weight.SEMI_BOLD};

  // HACK: Vertically center for now
  span {
    margin-top: 1px;
  }
`;

export const HalalComplianceModal: React.FC<RequireSomeFields<ModalProps, 'onClose'>> = (props) => {
  const { onClose, isShowing } = props;
  const { isSmallScreen } = useScreenContext();

  return (
    <StyledModal
      onClose={onClose}
      isShowing={isShowing}
      size='xl'
      isRounded={false}
      isFullWidth={isSmallScreen}
      isFullHeight={isSmallScreen}>
      <Header>
        <BackButtonContainer className='mr-2 text-xs' onClick={onClose}>
          <BackButton>
            <i className='fal fa-chevron-left' />
          </BackButton>
        </BackButtonContainer>
        <VerticallyCenteredText>Halal compliance</VerticallyCenteredText>
      </Header>
      <StyledModalBody>
        <Section background={Colors.JACKFRUIT_LITE_30}>
          <SectionTitle className='mb-2'>Serving our Halal community</SectionTitle>
          <SectionText className='mb-2'>
            To honor the diverse interpretations of Halal among Muslims, we have partnered with shefs to establish
            transparent communication about their food preparation methods.{' '}
          </SectionText>
          <SectionFootnote className='mb-3'>
            *For detailed ingredient information, please see dish item descriptions in the individual menus.
          </SectionFootnote>
          <div className='flex flex-row justify-around gap-4'>
            <img src={halalOuzieWithLegOfLamb} width={104} height={104} alt='Image of halal ouzi with leg of lamb' />
            <img src={halalLambTagine} width={104} height={104} alt='Image of halal lamb tagine' />
            <img src={halalKibbeh} width={104} height={104} alt='Image of halal kibbeh' />
          </div>
        </Section>

        <Section>
          <StyledChip
            className='mb-4'
            icon={<i className='fas fa-badge-check mr-2' />}
            text='Strict Halal kitchen'
            color={ChipColor.KOHLRABI}
            showShadow={false}
          />
          <SectionText className='mb-4'>A kitchen facility that exclusively prepares Halal meals.</SectionText>
          <ChipsJumble>
            <StyledChip text='Pork-free kitchen' color={ChipColor.LIGHT_GRAY} showShadow={false} />
            <StyledChip text='Halal certified meats only' color={ChipColor.LIGHT_GRAY} showShadow={false} />
            <StyledChip text='Halal certified gelatin' color={ChipColor.LIGHT_GRAY} showShadow={false} />
            <StyledChip text='Cross-contamination free' color={ChipColor.LIGHT_GRAY} showShadow={false} />
          </ChipsJumble>
        </Section>
        <Divider />
        <Section>
          <StyledChip
            className='mb-4'
            icon={<i className='fas fa-badge-check mr-2' />}
            text='Mixed Halal Kitchen'
            color={ChipColor.KOHLRABI}
            showShadow={false}
          />
          <SectionText className='mb-4'>
            A dual-purpose kitchen that maintains distinct preparation processes for both Halal and non-Halal meals.
          </SectionText>
          <ChipsJumble className='mb-4'>
            <StyledChip text='Pork-free menu' color={ChipColor.LIGHT_GRAY} showShadow={false} />
            <StyledChip text='Halal certified meats only' color={ChipColor.LIGHT_GRAY} showShadow={false} />
            <StyledChip text='Halal certified gelatin' color={ChipColor.LIGHT_GRAY} showShadow={false} />
          </ChipsJumble>
          <SectionFootnote>
            May use ingredients containing trace amounts of alcohol, seafoods including shellfish or fish without scales
            or be prepared in the same facility as non-Halal food.
          </SectionFootnote>
        </Section>
      </StyledModalBody>
    </StyledModal>
  );
};
